import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { QueryParams } from '@/model';
import { CalendarEventsService } from '../instances/calendar-events';
import { CalendarEventConverter } from '@/converters';

export const useCalendarEvents = (params?: QueryParams) => {
  const query = useQuery(
    ['GET_CALENDAR_EVENTS', params],
    async () => {
      const [err, data] = await CalendarEventsService.getCalendarEvents(params);
      if (err) {
        throw err;
      }
      return {
        data: CalendarEventConverter.toDomainFromPaginatedResponse(data),
        meta: data.meta,
      };
    },
    {
      refetchOnWindowFocus: false,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useCalendarEventsById = (id: string) => {
  const query = useQuery(
    ['GET_CALENDAR_EVENTS_BY_ID', id],
    async () => {
      const [err, data] = await CalendarEventsService.getCalendarEventById(id);
      if (err) {
        throw err;
      }
      return CalendarEventConverter.toDomainFromByIdResponse(data);
    },
    {
      refetchOnWindowFocus: false,
      enabled: !!id,
    },
  );

  const forceRefetch = useCallback(() => {
    query.remove();
    query.refetch();
  }, [query]);

  const returnQuery = useMemo(() => ({ ...query, forceRefetch }), [forceRefetch, query]);
  return returnQuery;
};

export const useGetCalendarEventsById = () => {
  return useMutation({
    mutationKey: ['GET_CALENDAR_EVENTS_BY_ID'],
    mutationFn: async (uuid: string) => {
      const [err, data] = await CalendarEventsService.getCalendarEventById(uuid);
      if (err) {
        throw err;
      }
      return CalendarEventConverter.toDomainFromByIdResponse(data);
    },
  });
};

export const usePostCalendarEventsMutation = () => {
  return useMutation({
    mutationKey: ['POST_CALENDAR_EVENTS'],
    mutationFn: async (payload: any) => {
      const [err, data] = await CalendarEventsService.postCalendarEvent(payload);
      if (err) {
        throw err;
      }
      return CalendarEventConverter.toDomainFromPostResponse(data);
    },
  });
};

export const usePutCalendarEventsMutation = () => {
  return useMutation({
    mutationKey: ['PUT_CALENDAR_EVENTS'],
    mutationFn: async ({ id, payload }: any) => {
      const [err, data] = await CalendarEventsService.putCalendarEvent(id, payload);
      if (err) {
        throw err;
      }
      return CalendarEventConverter.toDomainFromPatchResponse(data);
    },
  });
};

export const useDeleteCalendarEventsMutation = () => {
  return useMutation({
    mutationKey: ['DELETE_CALENDAR_EVENTS'],
    mutationFn: async (id: string) => {
      const [err] = await CalendarEventsService.deleteCalendarEvent(id);
      if (err) {
        throw err;
      }
      return;
    },
  });
};
